import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {IsGranted} from '../../../../../../decorator/is-granted';
import {MetaTitle} from '../../../../../../decorator/meta-title';
import {Funnel} from '../../../../../../models/cms/funnel/funnel';
import {Paginator} from '../../../../../../tools/pagination/paginator';
import {TableSorter} from '../../../../../../tools/table-sorter/table-sorter';
import {LoaderService} from '../../../../../../services/loader/loader.service';
import {FunnelService} from '../../../../../../services/cms/funnel.service';
import {ConfirmDirective} from '../../../../../../directives/confirm/confirm.directive';
import {IsGrantedDirective} from '../../../../../../directives/is-granted/is-granted.directive';
import {PaginatorComponent} from '../../../../../layout/paginator/paginator.component';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {TableSorterHeaderDirective} from '../../../../../../directives/table-sorter/table-sorter-header.directive';
import {RouterLink} from '@angular/router';
import {TableSorterDirective} from '../../../../../../directives/table-sorter/table-sorter.directive';
import {SnackbarService} from '../../../../../../services/snackbar/snackbar.service';
import {AvailableDomainService} from '../../../../../../services/cms/available-domain.service';
import {forkJoin, Subject} from 'rxjs';
import {AvailableDomain} from '../../../../../../models/cms/available-domain';
import {DropdownSelectComponent} from '../../../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../../../layout/dropdown-select-option/dropdown-select-option.component';
import {debounceTime} from 'rxjs/operators';
import {ListFilters} from '../../../../../../tools/list-filters/list-filters';
import {ListFilterService} from '../../../../../../services/list-filter/list-filter.service';
import {Product} from '../../../../../../models/product';
import {ProductService} from '../../../../../../services/product/product.service';

@Component({
  selector: 'app-funnel-list',
  imports: [
    ConfirmDirective,
    IsGrantedDirective,
    PaginatorComponent,
    ReactiveFormsModule,
    TableSorterHeaderDirective,
    RouterLink,
    TableSorterDirective,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    FormsModule,
    FormsModule
  ],
  templateUrl: './funnel-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './funnel-list.component.scss'
})
@IsGranted(['FUNNEL_VIEW'])
@MetaTitle('Funnel List')
export class FunnelListComponent implements OnInit {
  funnels: Funnel[] = [];
  paginator: Paginator = new Paginator();
  tableSorter: TableSorter = new TableSorter('name', 'asc');

  products: Product[] = [];
  domains: AvailableDomain[] = [];

  private nameChange$ = new Subject<void>();
  private slugChange$ = new Subject<void>();

  filters: ListFilters;

  constructor(
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private funnelService: FunnelService,
    private domainService: AvailableDomainService,
    private productService: ProductService,
    private listFilterService: ListFilterService,
  ) {
    this.filters = listFilterService.createFilters({
      name: 'string',
      productId: 'number[]',
      domains: 'number[]',
      slug: 'string',
    }, this.paginator, this.tableSorter);
  }

  ngOnInit(): void {
    this.loader.startLoading();

    forkJoin([
      this.domainService.findAll({sort: 'domain'}),
      this.productService.findAll({sort: 'name'})
    ]).subscribe(([domains, products]) => {
      this.domains = domains;
      this.products = products;
      this.loader.stopLoading();
    });

    this.nameChange$.pipe(debounceTime(300)).subscribe(() => this.filters.onFilterChange());
    this.slugChange$.pipe(debounceTime(300)).subscribe(() => this.filters.onFilterChange());

    this.filters.init(() => this.fetchAll());
  }

  onNameChange(): void {
    this.nameChange$.next();
  }

  onSlugChange(): void {
    this.slugChange$.next();
  }

  fetchAll(): void {
    const params: any = {...this.filters.toParams()};

    this.loader.startLoading();
    this.funnelService.findAll(params, this.paginator, this.tableSorter).subscribe((funnels: Funnel[]) => {
      this.funnels = funnels;
      this.loader.stopLoading();
    });
  }

  delete(funnel: Funnel): void {
    if (funnel.id) {
      this.loader.startLoading();
      this.funnelService.delete(funnel.id).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Funnel has been deleted');
        this.fetchAll();
      });
    }
  }

  duplicate(funnel: Funnel): void {
    if (funnel.id) {
      this.loader.startLoading();
      this.funnelService.duplicate(funnel.id).subscribe({
        next: () => {
          this.loader.stopLoading();
          this.snackbar.success('Funnel has been duplicated');
          this.fetchAll();
        },
        error: () => {
          this.loader.stopLoading();
        }
      });
    }
  }

  getFunnelUrl(funnel: Funnel): string {
    let domain = funnel.domain?.domain;

    return 'https://' + domain + '/fl/' + funnel.slug;
  }
}
